import React, { useState } from "react";
import { message } from "antd";
import { StatusBadge } from "../../Dashboard";
import { ReactTransliterate, TriggerKeys } from "react-transliterate";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { changeTab, setCustomer } from "../../../redux/slices/CustomerSlice";
import { selectToggle } from "../../../redux/slices/NavigationSlice";

import CurrencyFormat from "react-currency-format";
import Measurements from "../../../components/measurement/Measurements";
import PrintBillModal from "../../../components/Bill/PrintBillModal";
import api from "../../../utils/axios";
import DownloadBillModal from "../../../components/Bill/DownloadBillModal";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const PAGE_SIZE = 20;

const Search = () => {
  const dispatch = useDispatch();
  const lang = useSelector(selectToggle);
  const { t } = useTranslation();

  // search hooks
  const [isNameValid, setIsNameValid] = useState(false);
  const [isMobileValid, setIsMobileValid] = useState(false);
  const [isBillValid, setIsBillValid] = useState(false);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [billNo, setBillNo] = useState("");
  const [tableData, setTableData] = useState([]);
  const [nameLoading, setNameLoading] = useState(false);
  const [billNoLoading, setBillNoLoading] = useState(false);
  const [mobileNoLoading, setMobileNoLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [isPrint, setIsPrint] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [order, setOrder] = useState();

  // pagination hooks
  const [allOrders, setAllOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);

  const fetchPage = (page) => {
    setPageLoading(true);
    api
      .get(`order?page=${page}&size=${PAGE_SIZE}`)
      .then(({ data }) => {
        const paged = data.body;
        setAllOrders(paged.content);
        setCurrentPage(paged.currentPage);
        setTotalPages(paged.totalPages);
        setTotalElements(paged.totalElements);
        setPageLoading(false);
      })
      .catch((error) => {
        setPageLoading(false);
        console.error(error);
      });
  };

  // load first page on mount
  React.useEffect(() => {
    fetchPage(0);
  }, []);

  // handlers
  const isName = (value) => {
    setNameLoading(value);
    setTableLoading(value);
  };

  const isMobile = (value) => {
    setMobileNoLoading(value);
    setTableLoading(value);
  };

  const isBill = (value) => {
    setBillNoLoading(value);
    setTableLoading(value);
  };

  const handleSearchByName = async () => {
    setTableData([]);
    setIsBillValid(false);
    setIsMobileValid(false);
    setIsNameValid(false);
    if (name) {
      isName(true);
      api
        .get(`order/filter/names/${name}`)
        .then(({ data }) => {
          if (data.body.length) {
            setTableData(data.body);
          } else {
            message.info(t("common.noData"));
          }
          isName(false);
          setName("");
        })
        .catch((error) => {
          isName(false);
          console.error(error);
        });
    } else {
      setIsNameValid(true);
    }
  };

  const handleSearchByMobile = async () => {
    setTableData([]);
    setIsBillValid(false);
    setIsMobileValid(false);
    setIsNameValid(false);
    if (mobile) {
      isMobile(true);
      mobile &&
        api
          .get(`order/filter/mobile/${mobile}`)
          .then(({ data }) => {
            if (data.body.length) {
              setTableData(data.body);
            } else {
              message.info(t("common.noData"));
            }
            isMobile(false);
            setMobile("");
          })
          .catch((error) => {
            isMobile(false);
            console.error(error);
          });
    } else {
      setIsMobileValid(true);
    }
  };

  const handleSearchByBill = async () => {
    setTableData([]);
    setIsBillValid(false);
    setIsMobileValid(false);
    setIsNameValid(false);
    if (billNo) {
      isBill(true);
      billNo &&
        api
          .get(`order/filter/bill/${billNo}`)
          .then(({ data }) => {
            if (data.body.length) {
              setTableData(data.body);
            } else {
              message.info(t("common.noData"));
            }
            isBill(false);
            setBillNo("");
          })
          .catch((error) => {
            isBill(false);
            console.error(error);
          });
    } else {
      setIsBillValid(true);
    }
  };

  const handleEdit = (data, type) => {
    const order = {
      data,
      type,
    };

    setTimeout(() => {
      dispatch(setCustomer({ order }));
    }, 500);

    setTimeout(() => {
      dispatch(changeTab({ key: "add" }));
    }, 500);
  };

  const handleGenerateBill = (o) => {
    setIsPrint(true);
    setOrder(o);
  };

  const handleDownloadBill = (o) => {
    setIsDownload(true);
    setOrder(o);
  };

  const handleViewMeasurement = (o) => {
    setOrder(o);
  };

  const handleStatusChange = (o, newStatus) => {
    api
      .put(`/order/${o.billNo}/status?status=${newStatus}`)
      .then(() => {
        setTableData((prev) =>
          prev.map((r) => (r.billNo === o.billNo ? { ...r, status: newStatus } : r))
        );
        setAllOrders((prev) =>
          prev.map((r) => (r.billNo === o.billNo ? { ...r, status: newStatus } : r))
        );
        message.success(`${o.billNo} → ${newStatus}`);
      })
      .catch(() => message.error("Failed to update status"));
  };

  return (
    <section className="container-fluid">
      <div className="data-block">
        <div className="d-flex flex-column" style={{ gap: 10 }}>
          {/* search by name */}
          <div
            className="d-flex align-items-center"
            style={{ gap: 10 }}
          >
            <div style={{ width: 84, flexShrink: 0, textAlign: "right" }}>
              <label>{t("common.name")} :</label>
            </div>

            {/* name */}
            <div className="col-md-4">
              <ReactTransliterate
                value={name}
                onChangeText={(text) => {
                  setName(text);
                }}
                className="form-control"
                onFocus={(e) => e.target.select()}
                enabled={lang !== "en"}
                lang={lang}
                triggerKeys={[
                  TriggerKeys.KEY_RETURN,
                  TriggerKeys.KEY_ENTER,
                  TriggerKeys.KEY_SPACE,
                  TriggerKeys.KEY_TAB,
                ]}
              />
              {isNameValid ? (
                <p className="form-validation">{t("common.required")}</p>
              ) : null}
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={handleSearchByName}
                disabled={nameLoading}
              >
                {nameLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span className="m-2">{t("common.loading")}</span>
                  </>
                ) : (
                  <span className="m-2"> {t("common.find")}</span>
                )}
              </button>
            </div>
          </div>

          {/* search by mobile */}
          <div
            className="d-flex align-items-center"
            style={{ gap: 10 }}
          >
            <div style={{ width: 84, flexShrink: 0, textAlign: "right" }}>
              <label>{t("common.mobile")} :</label>
            </div>

            {/* mobile */}
            <div className="col-md-4">
              <input
                type="number"
                value={mobile}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  setMobile(e.target.value);
                }}
                className="form-control"
              />
              {isMobileValid ? (
                <p className="form-validation">{t("common.required")}</p>
              ) : null}
            </div>

            <div>
              <button
                className="btn btn-primary"
                onClick={handleSearchByMobile}
              >
                {mobileNoLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span className="m-2">{t("common.loading")}</span>
                  </>
                ) : (
                  <span className="m-2"> {t("common.find")}</span>
                )}
              </button>
            </div>
          </div>

          {/* search by bill no */}
          <div
            className="d-flex align-items-center"
            style={{ gap: 10 }}
          >
            <div style={{ width: 84, flexShrink: 0, textAlign: "right" }}>
              <label>{t("common.billNo")} :</label>
            </div>

            {/* bill no */}
            <div className="col-md-4">
              <input
                value={billNo}
                onChange={(e) => setBillNo(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="form-control"
              />
              {isBillValid ? (
                <p className="form-validation">{t("common.required")}</p>
              ) : null}
            </div>

            <div>
              <button className="btn btn-primary" onClick={handleSearchByBill}>
                {billNoLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span className="m-2">{t("common.loading")}</span>
                  </>
                ) : (
                  <span className="m-2"> {t("common.find")}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        <hr />
        {/* table */}
        {tableLoading ? (
          <div className="text-center">
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            <span className="m-2">{t("common.loading")}</span>
          </div>
        ) : (
          tableData &&
          tableData.length != 0 && (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t("common.billNo")}</th>
                    <th scope="col">{t("common.date")}</th>
                    <th scope="col">{t("common.name")}</th>
                    <th scope="col">{t("common.mobile")}</th>
                    <th scope="col">{t("common.pant")}</th>
                    <th scope="col">{t("common.shirt")}</th>
                    <th scope="col">{t("common.advance")}</th>
                    <th scope="col">{t("common.total")}</th>
                    <th scope="col">Status</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((order, index) => (
                    <tr key={order.billNo}>
                      <th scope="row">{order.billNo}</th>
                      <td>
                        {order.date && moment(order.date).format("DD/MM/yyyy")}
                      </td>
                      <td>{order.customer.name}</td>
                      <td>{order.customer.mobile}</td>
                      <td>
                        {order.customer.pant && order.customer.pant.pantCount
                          ? order.customer.pant.pantCount
                          : 0}
                      </td>
                      <td>
                        {order.customer.shirt && order.customer.shirt.shirtCount
                          ? order.customer.shirt.shirtCount
                          : 0}
                      </td>
                      <td>{order.advance ? order.advance : 0}</td>
                      <td>{order.amount ? order.amount : 0}</td>
                      <td>
                        <select
                          className="dp-status-select"
                          value={order.status || "RECEIVED"}
                          onChange={(e) => handleStatusChange(order, e.target.value)}
                        >
                          {["RECEIVED","CUTTING","STITCHING","READY","DELIVERED"].map(s => (
                            <option key={s} value={s}>{t(`status.${s}`)}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="d-flex">
                          <button
                            onClick={() => handleEdit(order, "save")}
                            className="btn"
                          >
                            <img
                              src="./images/save.png"
                              alt="edit"
                              width={20}
                            />{" "}
                            {t("common.save")}
                          </button>

                          <button
                            className="btn"
                            onClick={() => handleGenerateBill(order)}
                          >
                            <img
                              src="./images/invoice.png"
                              alt="bill"
                              width={20}
                            />{" "}
                            {t("common.generateBill")}
                          </button>

                          <button
                            className="btn"
                            onClick={() => handleDownloadBill(order)}
                          >
                            <img
                              src="./images/download.png"
                              alt="bill"
                              width={20}
                            />{" "}
                            {t("common.download")}
                          </button>

                          <button
                            type="button"
                            className="btn"
                            data-bs-toggle="modal"
                            data-bs-target="#measurementModal"
                            onClick={() => handleViewMeasurement(order)}
                          >
                            <img
                              src="./images/show.png"
                              alt="bill"
                              width={20}
                            />{" "}
                            {t("common.viewMeasure")}
                          </button>

                          <button
                            onClick={() => handleEdit(order, "edit")}
                            className="btn"
                          >
                            <img
                              src="./images/edit.png"
                              alt="edit"
                              width={20}
                            />{" "}
                            {t("common.update")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* All orders — paginated (hidden while a search has results showing) */}
      {tableData.length === 0 && (
      <div className="data-block mt-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>
            {t("common.allRecords")} ({totalElements})
          </strong>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage === 0 || pageLoading}
              onClick={() => fetchPage(currentPage - 1)}
            >
              ‹ {t("common.prev")}
            </button>
            <span style={{ fontSize: 13 }}>
              {currentPage + 1} / {totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage + 1 >= totalPages || pageLoading}
              onClick={() => fetchPage(currentPage + 1)}
            >
              {t("common.next")} ›
            </button>
          </div>
        </div>

        {pageLoading ? (
          <div className="text-center py-3">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <span className="m-2">{t("common.loading")}</span>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t("common.billNo")}</th>
                  <th>{t("common.date")}</th>
                  <th>{t("common.name")}</th>
                  <th>{t("common.mobile")}</th>
                  <th>{t("common.pant")}</th>
                  <th>{t("common.shirt")}</th>
                  <th>{t("common.advance")}</th>
                  <th>{t("common.total")}</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {allOrders.map((o) => (
                  <tr key={o.billNo}>
                    <th scope="row">{o.billNo}</th>
                    <td>{o.date && moment(o.date).format("DD/MM/yyyy")}</td>
                    <td>{o.customer.name}</td>
                    <td>{o.customer.mobile}</td>
                    <td>{o.customer.pant?.pantCount ?? 0}</td>
                    <td>{o.customer.shirt?.shirtCount ?? 0}</td>
                    <td>{o.advance ?? 0}</td>
                    <td>{o.amount ?? 0}</td>
                    <td>
                      <select
                        className="dp-status-select"
                        value={o.status || "RECEIVED"}
                        onChange={(e) => handleStatusChange(o, e.target.value)}
                      >
                        {["RECEIVED","CUTTING","STITCHING","READY","DELIVERED"].map(s => (
                          <option key={s} value={s}>{t(`status.${s}`)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div className="d-flex">
                        <button className="btn btn-sm" onClick={() => handleEdit(o, "save")}>
                          <img src="./images/save.png" alt="edit" width={18} /> {t("common.save")}
                        </button>
                        <button className="btn btn-sm" onClick={() => handleGenerateBill(o)}>
                          <img src="./images/invoice.png" alt="bill" width={18} /> {t("common.generateBill")}
                        </button>
                        <button className="btn btn-sm" onClick={() => handleDownloadBill(o)}>
                          <img src="./images/download.png" alt="bill" width={18} /> {t("common.download")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          data-bs-toggle="modal"
                          data-bs-target="#measurementModal"
                          onClick={() => handleViewMeasurement(o)}
                        >
                          <img src="./images/show.png" alt="bill" width={18} /> {t("common.viewMeasure")}
                        </button>
                        <button className="btn btn-sm" onClick={() => handleEdit(o, "edit")}>
                          <img src="./images/edit.png" alt="edit" width={18} /> {t("common.update")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      <PrintBillModal order={order} isOpen={isPrint} setIsOpen={setIsPrint} />
      <DownloadBillModal
        order={order}
        isOpen={isDownload}
        setIsOpen={setIsDownload}
      />
      <Measurements order={order} />
    </section>
  );
};

export default Search;
